import React, { useState } from 'react';
import { getClubBannerPublicUrl, DEFAULT_CLUB_BANNER_URL } from '../../services/clubBannerService';
import ClubLogo from './ClubLogo';

/**
 * ClubBanner - Banner image component for club cards and club pages
 * 
 * Features:
 * - Fixed height with object-fit cover (no layout shift)
 * - Lazy loading for performance
 * - Gradient overlay for text readability
 * - Fallback default banner image when no custom banner or on load error
 * - Optional club name and location overlay
 * 
 * @param {Object} props
 * @param {string|null} props.bannerImageUrl - Storage path for banner (e.g., "{clubId}.jpg")
 * @param {string} [props.clubName] - Club name to display on banner
 * @param {string} [props.location] - Club location to display on banner
 * @param {string|null} [props.logoImageUrl] - Storage path for club logo (shown as small circle next to name)
 * @param {string} [props.variant] - Display variant ('card' | 'page'). Default: 'card'
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.imageHeightClass] - Optional Tailwind height override (e.g. responsive banner sizing)
 */
const ClubBanner = ({
  bannerImageUrl,
  clubName = '',
  location = '',
  logoImageUrl = null,
  variant = 'card',
  className = '',
  imageHeightClass,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const customUrl = bannerImageUrl ? getClubBannerPublicUrl(bannerImageUrl) : null;
  const displayUrl = customUrl && !imageError ? customUrl : DEFAULT_CLUB_BANNER_URL;
  const isDefaultBanner = displayUrl === DEFAULT_CLUB_BANNER_URL;
  
  // Variant-specific heights
  const heights = {
    card: 'h-32', // 128px / 8rem
    page: 'h-40', // 160px / 10rem
  };
  
  const height = imageHeightClass || heights[variant] || heights.card;

  const handleLoad = () => {
    setImageLoaded(true);
  };

  // Placeholder while custom/default banner loads
  const placeholderGradient = 'bg-gradient-to-br from-primary/20 via-accent/20 to-info/20';

  return (
    <div className={`relative ${height} overflow-hidden rounded-t-lg ${className}`}>
      <img
        src={displayUrl}
        alt={isDefaultBanner ? 'Default club banner' : `${clubName} banner`}
        loading="lazy"
        onError={() => {
          if (!isDefaultBanner) setImageError(true);
        }}
        onLoad={handleLoad}
        className={`
          w-full h-full object-cover
          transition-opacity duration-300
          ${imageLoaded ? 'opacity-100' : 'opacity-0'}
        `}
      />

      {!imageLoaded && (
        <div className={`absolute inset-0 ${placeholderGradient} animate-pulse`} />
      )}
      
      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      
      {/* Club name and location overlay */}
      {(clubName || location) && (
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          {clubName && (
            <div className="flex items-center gap-2 mb-0.5">
              {logoImageUrl && (
                <ClubLogo
                  logoImageUrl={logoImageUrl}
                  clubName={clubName}
                  size="sm"
                  className="border-2 border-white/30 shadow-sm shrink-0"
                />
              )}
              <h3 className="text-lg font-bold text-white drop-shadow-md leading-tight">
                {clubName}
              </h3>
            </div>
          )}
          {location && (
            <p className={`text-sm text-white/90 drop-shadow-md ${logoImageUrl ? 'pl-10' : ''}`}>
              {location}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ClubBanner;
